import { ContactSlugEnum, FormInputSearch } from '@systemeio/ui'
import { defaultSearchFilterValue } from 'modules/contacts/constants/contact-default-filter'
import { ContactsLoco } from 'modules/contacts/constants/contacts-loco'
import { ContactSearchFieldEnum } from 'modules/contacts/enums/contact-enum'
import { useApplyFilterWithConditions } from 'modules/contacts/hooks/use-apply-filter-with-conditions'
import { useAssistantContacts } from 'modules/contacts/hooks/use-assistant-contacts'
import { useContactFields } from 'modules/contacts/hooks/use-contact-fields'
import { useContactFilterPagination } from 'modules/contacts/hooks/use-contact-filter-pagination'
import { useFilterWithConditions } from 'modules/contacts/hooks/use-filter-with-conditions'
import {
  ContactFilterTextInterface,
  ContactFilterType,
} from 'modules/contacts/types/contact-filter-interface'
import { ContactInterface } from 'modules/contacts/types/contact-interface'
import { NextPage } from 'next'
import React from 'react'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { OrderEnum } from 'shared/types/pagination-interface'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'

const defaultFilters: ContactFilterType = {
  [ContactSlugEnum.Email]: [{ ...defaultSearchFilterValue }],
  [ContactSearchFieldEnum.affiliate]: [{ ...defaultSearchFilterValue }],
}

const AssistantContacts: NextPage = () => {
  const { setConditions, conditions, filter, setFilter, filterFields, filtersReady } =
    useFilterWithConditions(defaultFilters)
  const { setPagination, pagination, setLimit, limit } = useContactFilterPagination({
    conditions,
    setConditions,
  })

  const { t } = useLocoTranslation()
  const { user } = useUser()

  const { fields } = useContactFields()

  const { contacts, counter, abortContacts } = useAssistantContacts({
    limit,
    pagination,
    conditions,
  })

  useApplyFilterWithConditions({
    abort: abortContacts,
    filterFields,
    filtersReady,
    setLimit,
    setConditions,
    conditions,
    filter,
    onFilterChange: () => setPagination({ startFromId: undefined, order: OrderEnum.next }),
    delay: 0,
  })

  const columns: ColumnDefinitionInterface<ContactInterface>[] = [
    {
      key: 'registeredAt',
      header: t('dashboard.contacts.date_registered'),
      onRender: contact => (
        <span>
          {dateTimeFormat(contact.registeredAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        </span>
      ),
      widthPercent: 30,
    },
    {
      key: 'fieldValues',
      header: t('dashboard.user.first_name'),
      onRender: contact => (
        <div className="truncate">
          {contact.fieldValues.find(el => el.contactField.slug === ContactSlugEnum.FirstName)
            ?.value || ''}
        </div>
      ),
      widthPercent: 30,
    },
    {
      key: 'email',
      header: t('global.email_address'),
      widthPercent: 40,
      onRender: contact => (
        <div className="truncate">
          <LinkWithoutPrefetch className="primary-link w-full" href={`/contacts/${contact.id}`}>
            {contact.email}
          </LinkWithoutPrefetch>
        </div>
      ),
    },
  ]

  return (
    <>
      <ListingLayout
        data={contacts}
        title={t('dashboard.contacts.title_plural')}
        type={'compact'}
        counter={counter}
        filterData={{
          limiter: { limit, setLimit },
          paginator: { pagination, setPagination },
          filterer: {
            filter,
            setFilter,
            defaultFilters,
          },
          filterable: [
            {
              field: ContactSlugEnum.Email,
              onRender: (value, onChange) => {
                const email = value[0] as ContactFilterTextInterface
                return (
                  <FormInputSearch
                    key={'email-filter'}
                    value={email.searchValue}
                    type={'email'}
                    onClear={() =>
                      onChange([{ compareOperation: email.compareOperation, searchValue: '' }])
                    }
                    onChange={e =>
                      onChange([
                        { compareOperation: email.compareOperation, searchValue: e.target.value },
                      ])
                    }
                    placeholder={t('global.email')}
                    isPreFetching={!fields}
                  />
                )
              },
            },
            {
              field: ContactSearchFieldEnum.affiliate,
              onRender: (value, onChange) => {
                const affiliate = value[0] as ContactFilterTextInterface
                return (
                  <FormInputSearch
                    key={'affiliate-filter'}
                    value={affiliate.searchValue}
                    onClear={() =>
                      onChange([{ compareOperation: affiliate.compareOperation, searchValue: '' }])
                    }
                    onChange={e =>
                      onChange([
                        {
                          compareOperation: affiliate.compareOperation,
                          searchValue: e.target.value,
                        },
                      ])
                    }
                    placeholder={t('dashboard.contacts.filter.affiliate_id')}
                    isPreFetching={!fields}
                  />
                )
              },
            },
          ],
        }}
      >
        <Table
          data={contacts}
          columns={columns}
          limiter={{ limit, setLimit }}
          paginator={{ pagination, setPagination }}
        />
      </ListingLayout>
    </>
  )
}

export const getServerSideProps = getBaseServerSideProps(ContactsLoco, 'header.top_menu.contacts')

export default AssistantContacts
